/**
 * ViewHealthBadge — the active view's OWN health, in the board HUD beside the
 * view picker. `planView` reports unresolved refs, sort mismatches, unknown
 * policy/knob, and (defensively) a projection crash as `plan.diagnostics`; those
 * used to vanish. This surfaces them as a small pill with a count that expands to
 * the list, so a view that silently drops half its clauses is now visible.
 *
 * Renders nothing when the active view is clean (or none is active), so it costs
 * no HUD space in the common case. Matches the HUD pill styling (see `HudChip`).
 */
import type { ReactNode } from "react";
import { useViewPlanStatus } from "./ViewPlanStatusContext.js";

export function ViewHealthBadge(): ReactNode {
  const diagnostics = useViewPlanStatus();
  if (diagnostics.length === 0) return null;
  const errors = diagnostics.filter((d) => d.severity === "error").length;
  // Red pill if the view failed outright (errors present), amber for warnings.
  const accent = errors > 0
    ? "bg-red-500/20 border-red-400/70 text-red-200 hover:bg-red-500/30"
    : "bg-amber-500/20 border-amber-400/70 text-amber-200 hover:bg-amber-500/30";
  const label = errors > 0 ? `${errors} view error${errors === 1 ? "" : "s"}` : `${diagnostics.length} view issue${diagnostics.length === 1 ? "" : "s"}`;
  return (
    <details data-hud className="relative inline-block">
      <summary
        className={
          "inline-flex cursor-pointer list-none items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs " +
          "shadow-lg backdrop-blur-md whitespace-nowrap transition-colors " +
          accent
        }
        title="Active view diagnostics"
      >
        <span aria-hidden>⚠</span>
        {label}
      </summary>
      <div
        data-hud
        className="absolute right-0 z-chrome mt-1 max-h-72 w-80 overflow-y-auto rounded-lg border border-slate-700 bg-slate-900/(--tint-solid) p-2 text-xs shadow-2xl backdrop-blur-md"
      >
        <ul className="space-y-1">
          {diagnostics.map((d, i) => (
            <li key={`${d.code}:${i}`} className="leading-snug">
              <span
                className={d.severity === "error" ? "text-red-300" : "text-amber-300"}
              >
                {d.message}
              </span>
              <span className="ml-1 text-slate-500">[{d.code}]</span>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}
